import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { getRequest } from "@tanstack/react-start/server";

const auditEventTypes = [
  "auth.signin.failed_lockout",
  "auth.signin.success",
  "auth.signin.failed",
  "auth.signout",
  "auth.password.reset_requested",
  "payments.checkout.created",
  "payments.subscription.updated",
  "admin.role.granted",
  "admin.role.revoked",
  "admin.kyc.approved",
  "admin.kyc.rejected",
  "admin.message.status_changed",
  "admin.message.deleted",
  "admin.loan_listing.updated",
  "admin.invest_plan.updated",
  "rls.policy.changed",
  "access.suspicious",
  "access.unauthorized",
  "access.forbidden",
] as const;

export type SecurityAuditEventType = (typeof auditEventTypes)[number];

// Anonymous callers may only log pre-auth lifecycle events. All other types
// require an authenticated session so the actor cannot be spoofed.
const anonymousEventTypes = [
  "auth.signin.failed_lockout",
  "auth.signin.success",
  "auth.signin.failed",
  "auth.signout",
  "auth.password.reset_requested",
] as const;

const detailsSchema = z
  .record(z.unknown())
  .refine((v) => JSON.stringify(v).length <= 4096, "details too large")
  .default({});

const anonymousAuditInput = z.object({
  event_type: z.enum(anonymousEventTypes),
  details: detailsSchema,
});

const authenticatedAuditInput = z.object({
  event_type: z.enum(auditEventTypes),
  details: detailsSchema,
});

function extractIp(): string | null {
  try {
    const req = getRequest();
    const h = req?.headers;
    if (!h) return null;
    const fwd = h.get("x-forwarded-for");
    if (fwd) return fwd.split(",")[0].trim();
    return h.get("cf-connecting-ip") || h.get("x-real-ip") || null;
  } catch {
    return null;
  }
}

async function insertAuditRow(event_type: string, details: Record<string, unknown>, user_id: string | null) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { error } = await supabaseAdmin.from("security_audit_log").insert({
    event_type,
    details: details as never,
    user_id,
    ip_address: extractIp(),
  });
  if (error) console.error("security_audit_log insert failed:", error);
}

// Public entrypoint — restricted to anonymous auth.* events; actor_id ignored.
export const logSecurityEvent = createServerFn({ method: "POST" })
  .inputValidator((data) => anonymousAuditInput.parse(data))
  .handler(async ({ data }) => {
    await insertAuditRow(data.event_type, data.details as Record<string, unknown>, null);
    return { ok: true };
  });

// Authenticated entrypoint — any event type; actor is the verified caller.
export const logAuthenticatedSecurityEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => authenticatedAuditInput.parse(data))
  .handler(async ({ data, context }) => {
    await insertAuditRow(data.event_type, data.details as Record<string, unknown>, context.userId);
    return { ok: true };
  });

const listInput = z.object({
  limit: z.number().int().min(1).max(500).default(200),
  event_type: z.enum(auditEventTypes).optional(),
  since: z.string().datetime().optional(),
});

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type SecurityAuditRow = {
  id: string;
  event_type: string;
  user_id: string | null;
  ip_address: string | null;
  details: JsonValue;
  created_at: string;
};

export const listSecurityAuditLog = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => listInput.parse(data ?? {}))
  .handler(async ({ data, context }): Promise<{ rows: SecurityAuditRow[] }> => {
    const { data: isAdmin, error: roleErr } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (roleErr || !isAdmin) {
      // Log forbidden access attempt
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("security_audit_log").insert({
          event_type: "access.forbidden",
          user_id: context.userId,
          ip_address: extractIp(),
          details: { resource: "security_audit_log" },
        });
      } catch {}
      throw new Error("Forbidden");
    }

    let q = context.supabase
      .from("security_audit_log")
      .select("id,event_type,user_id,ip_address,details,created_at")
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.event_type) q = q.eq("event_type", data.event_type);
    if (data.since) q = q.gte("created_at", data.since);

    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { rows: (rows ?? []) as SecurityAuditRow[] };
  });
